import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { FlexColumnContainer } from "@trailhead/core-ui";
import {
  Button as AsurionButton,
  Text,
  ButtonVariant,
  breakpoints,
  Icon,
} from "@trailhead/asurion-ui";
import { trackEvent } from "@trailhead/analytics";
import PhoneWithIcons from "../assets/PhoneWithIcons.png";
import { useSanitizedTranslation } from "@trailhead/i18n";
import { navigateToUrl } from "single-spa";
import { useQueryParams } from "@trailhead/smart-setup-core";


const IntroHeaderContainer = styled(FlexColumnContainer)`
  justify-content: space-evenly;
  align-self: center;
  flex-direction: column;
  position: relative;
  width: 100%;
  min-height: 280px;
  color: #000000;
  gap: 25px;
  margin-bottom: 50px;
`;

const HeroSection = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;

  ${breakpoints.mdMax} {
    flex-direction: column-reverse;
    justify-content: center;
    align-items: center;
  }
`;

const HeaderTitle = styled(Text)``;

const TitleContainer = styled.div`
  margin-top: 0;
  display: flex;
  flex-direction: column;
  flex: 1;
  flex-shrink: 2;
  justify-content: center;
  z-index: 1;
  gap: 1.25rem;

  ${breakpoints.md} {
    width: 50%;
  }

  ${breakpoints.mdMax} {
    align-items: center;
    text-align: center;
  }
`;

const AsurionButtonWrapper = styled(AsurionButton)`
  height: auto;
  max-height: 3rem;
`;

const SelfGuidedSetupWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
  z-index: 1;

  ${breakpoints.md} {
    justify-content: start;
  }
`;

const IconContainer = styled.div`
  padding-right: 0.75rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ImageContainer = styled.div`
  display: flex;
  height: 100%;
  justify-content: center;

  ${breakpoints.md} {
    width: 50%;
  }
`;

const SelfServiceImage = styled.img`
  height: 100%;
  object-fit: contain;

  ${breakpoints.mdMax} {
    max-height: 270px;
  }
`;

type HeaderType = "bold" | "normal";

interface IntroHeaderProps {
  buttonText: string;
  buttonCallback?: () => void;
  headerType?: HeaderType;
  buttonVariant?: ButtonVariant;
  showCallButton?: boolean;
  callButtonPhoneNumber?: string;
}

export const IntroHeader: React.FC<IntroHeaderProps> = ({
  buttonText,
  buttonCallback,
  headerType = "standard",
  buttonVariant = "default",
  showCallButton = false,
  callButtonPhoneNumber,
}) => {
  const t = useSanitizedTranslation();
  const queryParams = useQueryParams();
  const [callNumber, setCallNumber] = useState(callButtonPhoneNumber);

  const defaultCallback = useCallback(() => {
    trackEvent("SelfGuideNavigationButtonClick");
    queryParams.set("skipPathfinder", "true");

    navigateToUrl(`/mobile-device-setup/intro?${queryParams.toString()}`);
  }, []);

  const defaultSupportNumber = "866-314-7861";
  const supportNumber = defaultSupportNumber;

  useEffect(() => {
    if (
      typeof callNumber === "undefined" ||
      supportNumber !== defaultSupportNumber
    ) {
      setCallNumber(supportNumber);
    }
  }, [supportNumber]);

  return (
    <IntroHeaderContainer>
      <HeroSection>
        <TitleContainer>
          <HeaderTitle
            size={5}
            weight={headerType === "bold" ? "heavy" : "base"}
          >
            {t("Set up your device with expert help. ")}
            <Text size={5} weight={"heavy"}>
              {t("We’re here for you.")}
            </Text>
          </HeaderTitle>
          <SelfGuidedSetupWrapper>
            <AsurionButtonWrapper
              size="medium"
              variant={buttonVariant}
              onClick={buttonCallback ?? defaultCallback}
            >
              <>
                <IconContainer>
                  <Icon size="large" src="ChatBubbleCircle" fill="#ffffff" />
                </IconContainer>
                {t(buttonText)}
              </>
            </AsurionButtonWrapper>
            {showCallButton && (
              <AsurionButtonWrapper
                size="medium"
                color="secondary"
                variant="outline"
                onClick={() => {
                  window.open(`tel:${callNumber}`, "_self"); // open for both desktop and mobile for now
                  trackEvent("Pathfinder_Call_Prompt_Click", {
                    buttonText: `Call ${callNumber}`,
                  });
                }}
              >
                <>
                  <IconContainer>
                    <Icon size="large" src="Phone" />
                  </IconContainer>
                  {callNumber}
                </>
              </AsurionButtonWrapper>
            )}
          </SelfGuidedSetupWrapper>
        </TitleContainer>
        <ImageContainer>
          <SelfServiceImage src={PhoneWithIcons} />
        </ImageContainer>
      </HeroSection>
    </IntroHeaderContainer>
  );
};
